/*
 * ==============================================================================
 *
 * Copyright (c) 2023. Koninklijke Philips Electronics N.V.
 *
 * All rights are reserved. Reproduction in whole or in part is prohibited without the prior written consent of the copyright holder.
 *
 * ==============================================================================
 */

import { useState, useMemo, useContext } from "react";
import { PAMV1RestUrl } from "../../constants/PatientManagementConstants";
import { PatientSyncStatus as Status } from "../../constants/PatientSyncStatus";
import fetchHelper from "../../services/fetchHelper";
import { LayoutContext } from "../layout";
import { PatientsContext } from "../../pages/patients";
import { featureCheck } from "../../utils/feature";

const asyncSupportEnabled = featureCheck("patientSync.enableAsyncSupport");
const PSyncQueryBaseUrl = `${PAMV1RestUrl}/psync${!asyncSupportEnabled ? "/lock" : ""}`;

const useSyncHandler = () => {
  const { currentLocation } = useContext(LayoutContext);
  const { lastSyncUpdate, setLastSyncUpdate } = useContext(PatientsContext);
  const [syncResult, setSyncResult] = useState(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [hasSyncError, setHasSyncError] = useState(false);
  const isSyncSupported = useMemo(() => currentLocation?.rls || currentLocation?.fhir, [currentLocation]);

  const syncStatus = useMemo(() => {
    let status = Status.NOTREADY;

    if (isSyncing) {
      status = Status.SYNCING;
    } else if (hasSyncError) {
      status = Status.WARNING;
    } else if (syncResult) {
      status = syncResult.locked ? Status.LOCKED : Status.UNLOCKED;
    }
    return status;
  }, [syncResult, isSyncing]);

  const hasSyncUpdate = useMemo(() => {
    let ret = syncResult?.hasUpdate;
    if (asyncSupportEnabled && syncResult && setLastSyncUpdate) {
      const curUpdate = Date.parse(syncResult.updatedAt);
      ret = lastSyncUpdate ? lastSyncUpdate < curUpdate : ret;
      setLastSyncUpdate(curUpdate);
    }

    return ret;
  }, [syncResult]);

  const dispatchSync = async (patient) => {
    const patientMrn = patient?.patientLocation[0].mrn;
    const hasRequiredInfo = !!patientMrn || (patient?.dob && patient?.name && patient?.family);

    if (!syncResult && !isSyncing && hasRequiredInfo) {
      try {
        setIsSyncing(true);
        setHasSyncError(false);
        setSyncResult(null);
        const data = await fetchHelper.get({
          url: `${PSyncQueryBaseUrl}/${patient.id}`,
        });
        setSyncResult(data);
      } catch (err) {
        console.log(err);
        setHasSyncError(true);
      } finally {
        setIsSyncing(false);
      }
    }
  };

  return { syncStatus, syncResult, dispatchSync, isSyncSupported, hasSyncUpdate };
};

export default useSyncHandler;
